import { useTranslation } from "react-i18next";
import { createHashRouter, Navigate, RouterProvider } from "react-router-dom";
import { RouteErrorBoundary } from "@/components/error-page/RouteErrorBoundary";
import { CashierPageWrapper } from "@/components/general/cashier-page-wrapper/CashierPageWrapper";
import { MFEWrapper } from "@/components/general/mfe-wrapper/MFEWrapper";
import { Accounts } from "@/pages/deposit/accounts/Accounts";
import { ChooseMethod } from "@/pages/deposit/choose-method/ChooseMethod";
import { Completion } from "@/pages/deposit/completion/Completion";
import { LoadingPage } from "@/pages/deposit/loading-page/LoadingPage";
import { TransactionHistoryDetails } from "@/pages/history/transactions/TransactionHistoryDetails";
import { Transactions } from "@/pages/history/transactions/Transactions";
import { TransferCompletion } from "@/pages/transfer/completion";
import { InternalTransfer } from "@/pages/transfer/internal-transfer/InternalTransfer";
import { Accounts as WithdrawalAccounts } from "@/pages/withdrawals/accounts/Accounts";
import { ChooseMethod as WithdrawalChooseMethod } from "@/pages/withdrawals/choose-method/ChooseMethod";
import { WithdrawalCompletion } from "@/pages/withdrawals/completion/WithdrawalComplention";
import { Summary } from "@/pages/withdrawals/summary/Summary";
import { ECashierFlows, ERoutes } from "@/utils/enums";
import { BankTransfer } from "@/pages/deposit/bank-transfer/BankTransfer";
import { WithdrawalVerification } from "@/pages/withdrawals/completion/WithdrawalVerification";

export const Router = () => {
  const { t } = useTranslation();

  const router = createHashRouter([
    {
      path: "/",
      element: <MFEWrapper />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          index: true,
          element: <Navigate to={ECashierFlows.Deposit} />,
        },
        {
          path: ECashierFlows.Deposit,
          element: <CashierPageWrapper title={t("transactions.funds.title")} />,
          children: [
            { index: true, element: <Navigate to={ERoutes.Accounts} /> },
            {
              // landing page for public-site redirects
              path: ":scaAccountLoginId",
              element: <Accounts />,
            },
            {
              path: ERoutes.Accounts,
              element: <Accounts />,
            },
            {
              path: `:accountLoginId/${ERoutes.ChooseMethod}`,
              element: <ChooseMethod />,
            },
            {
              path: `:accountLoginId/${ERoutes.LoadingPage}`,
              element: <LoadingPage />,
            },
            {
              path: `:accountLoginId/${ERoutes.BankTransfer}`,
              element: <BankTransfer />,
            },
            {
              path: `${ERoutes.Completion}/:id`,
              element: <Completion transactionType="deposit" />,
            },
            {
              // Bank transfer: no referenceId, data passed via location.state
              path: ERoutes.Completion,
              element: <Completion transactionType="deposit" />,
            },
          ],
        },
        {
          path: ECashierFlows.History,
          element: (
            <CashierPageWrapper title={t("transactions.history.title")} />
          ),
          children: [
            { index: true, element: <Transactions /> },
            {
              path: ":type/:id",
              element: <TransactionHistoryDetails />,
            },
          ],
        },
        {
          path: ECashierFlows.Withdraw,
          element: <CashierPageWrapper title={t("transactions.funds.title")} />,
          children: [
            { index: true, element: <Navigate to={ERoutes.Accounts} /> },
            { path: ERoutes.Accounts, element: <WithdrawalAccounts /> },
            { path: ERoutes.ChooseMethod, element: <WithdrawalChooseMethod /> },
            { path: ERoutes.Summary, element: <Summary /> },
            {
              path: `${ERoutes.Completion}/:id`,
              element: <WithdrawalCompletion />,
            },
            {
              path: `${ERoutes.ErrorPage}`,
              element: <WithdrawalCompletion />,
            },
            {
              path: `${ERoutes.Verification}/:id`,
              element: <WithdrawalVerification />,
            },
          ],
        },
        {
          path: ECashierFlows.Transfer,
          element: <CashierPageWrapper title={t("transfer.title")} />,
          children: [
            { index: true, element: <InternalTransfer /> },
            {
              path: `${ERoutes.CompletionSuccess}/:id`,
              element: <TransferCompletion />,
            },
            {
              path: `${ERoutes.CompletionFailed}/:id`,
              element: <TransferCompletion />,
            },
          ],
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
};
